// src/components/views/BusinessLogView.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ClipboardList } from 'lucide-react';

const BusinessLogView = ({ 
  business, 
  onBack, 
  filteredLogs, 
  logsLoading, 
  logsError, 
  logsErrorState, 
  logsLoadingState,
  addLogLoading,
  addLogError,
  onAddLog 
}) => {
  // Local state for the form
  const [formData, setFormData] = useState({
    contact_person: '',
    log_date: new Date().toISOString().split('T')[0],
    log_type: 'call',
    log_subject: '',
    log_notes: ''
  });

  // Refs for inputs
  const contactPersonRef = useRef(null);
  const logSubjectRef = useRef(null);
  const logNotesRef = useRef(null);

  // Focus first input on mount
  useEffect(() => {
    if (contactPersonRef.current) {
      contactPersonRef.current.focus();
    }
  }, []);

  // Handle local input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!business) {
      return;
    }

    if (!formData.contact_person.trim() || !formData.log_subject.trim()) {
      return;
    }

    const logToSubmit = {
      ...formData,
      client_id: business.client_id,
      business_id: business.business_id
    };

    await onAddLog(logToSubmit);
    
    // Reset form only on success
    setFormData({
      contact_person: '',
      log_date: new Date().toISOString().split('T')[0],
      log_type: 'call',
      log_subject: '',
      log_notes: ''
    });
    
    // Refocus the first input
    if (contactPersonRef.current) {
      contactPersonRef.current.focus();
    }
  }, [formData, business, onAddLog]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Sort logs by date (newest first)
  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const dateA = new Date(a.log_date);
    const dateB = new Date(b.log_date);
    return dateB - dateA;
  });

  return (
    <div>
      <div className="mb-6">
        <button 
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center space-x-2"
        >
          <ArrowLeft size={20} />
          <span>Back to Business Collection</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-800">Business Log</h1>
        <p className="text-gray-600">View and manage business activity logs</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600 font-medium">Business ID</label>
            <p className="font-semibold text-gray-900">{business?.business_id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600 font-medium">Business Name</label>
            <p className="font-semibold text-gray-900">{business?.business_name}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600 font-medium">Client ID</label>
            <p className="font-semibold text-gray-900">{business?.client_id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-600 font-medium">Industry Type</label>
            <p className="font-semibold text-gray-900">{business?.industry_type}</p>
          </div>
        </div>
      </div>

      {(logsError || logsErrorState) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Error:</strong> {logsError || logsErrorState}
        </div>
      )}

      {(logsLoading || logsLoadingState) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading business logs...</p>
        </div>
      )}

      {!logsLoading && !logsLoadingState && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Contact Person Logs</h3>
            <p className="text-sm text-gray-600 mt-1">
              Logs for: <span className="font-semibold">{business?.business_name}</span>
            </p>
          </div>

          <div className="p-6">
            {/* ADD LOG FORM */}
            <div className="mb-8 bg-blue-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">Add New Log Entry</h4>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <input
                    ref={contactPersonRef}
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    placeholder="Enter contact person"
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    required
                  />

                  <input
                    type="date"
                    name="log_date"
                    value={formData.log_date}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    required
                  />

                  <select
                    name="log_type"
                    value={formData.log_type}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="call">Call</option>
                    <option value="meeting">Meeting</option>
                    <option value="email">Email</option>
                    <option value="visit">Visit</option>
                    <option value="note">Note</option>
                  </select>

                  <input
                    ref={logSubjectRef}
                    type="text"
                    name="log_subject"
                    value={formData.log_subject}
                    onChange={handleInputChange}
                    placeholder="Enter subject"
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <textarea
                  ref={logNotesRef}
                  name="log_notes"
                  value={formData.log_notes}
                  onChange={handleInputChange}
                  placeholder="Enter notes"
                  autoComplete="off"
                  rows={3}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                />

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {addLogLoading && <span className="text-blue-600">Adding log...</span>}
                    {addLogError && <span className="text-red-600">{addLogError}</span>}
                  </div>
                  <button
                    type="submit"
                    disabled={addLogLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors font-medium"
                  >
                    {addLogLoading ? "Adding..." : "Add Log"}
                  </button>
                </div>
              </form>
            </div>

            {/* LOG TABLE */}
            {sortedLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Contact Person</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedLogs.map((log) => (
                      <tr key={log.log_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {log.contact_person}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {formatDate(log.log_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            log.log_type === 'call' ? 'bg-blue-100 text-blue-800' :
                            log.log_type === 'meeting' ? 'bg-green-100 text-green-800' :
                            log.log_type === 'email' ? 'bg-purple-100 text-purple-800' :
                            log.log_type === 'visit' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.log_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {log.log_subject}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {log.log_notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 text-sm text-gray-600">
                  Showing {sortedLogs.length} log{sortedLogs.length !== 1 ? 's' : ''}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 border border-gray-200 rounded-lg bg-gray-50">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <ClipboardList className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No logs found</h3>
                <p className="text-gray-500 mb-4">Add your first log using the form above.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessLogView;